import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { QueryResultComponent, QueryRunStatus } from './query-result.component';

export interface QueryTestData {
    query: string;
}

/**
 * One query's result, over whatever opened it.
 *
 * Testing from the editor used to push the result in under the query box, which
 * left the thing being edited and the thing being judged competing for the same
 * screen. As a dialog it sits above, at a size worth looking at, and closing it
 * returns to the editor exactly as it was — nothing here touches the draft.
 */
@Component({
    selector: 'app-query-test-dialog',
    template: `
        <h2 mat-dialog-title>
            Test result
            <span class="meta mono" [class.bad]="failed">{{ meta }}</span>
        </h2>

        <mat-dialog-content class="content">
            <app-query-result #result [query]="data.query" (statusChange)="onStatus($event)"></app-query-result>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button (click)="rerun()" [disabled]="running">Run again</button>
            <!-- The query's own URL with a short-lived token, which is what another
                 application would put in an iframe. -->
            <button
                mat-button
                (click)="result.openInNewTab()"
                [title]="
                    result.embedError
                        ? 'Last attempt failed: ' + result.embedError
                        : result.embedExpiresAt
                          ? 'Open this query as a URL with an embed token (last link valid until ' + result.embedExpiresAt + ')'
                          : 'Open this query as a URL with an embed token, valid as long as the server allows'
                "
            >
                Open as URL
            </button>
            <button mat-flat-button color="primary" mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            :host {
                display: block;
                width: min(1100px, 92vw);
            }

            h2 {
                display: flex;
                align-items: baseline;
                gap: 10px;
            }

            .meta {
                font-size: 11px;
                font-weight: 400;
                color: rgba(0, 0, 0, 0.6);
            }

            .meta.bad {
                color: #b3261e;
            }

            /* The result fills the dialog body; it sizes itself from this height. */
            .content {
                height: min(70vh, 720px);
                max-height: none;
                padding: 0;
                overflow: hidden;
            }

            app-query-result {
                display: block;
                width: 100%;
                height: 100%;
            }

            .mono {
                font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            }
        `,
    ],
})
export class QueryTestDialogComponent implements AfterViewInit {
    @ViewChild(QueryResultComponent) private result?: QueryResultComponent;

    public meta = '';
    public failed = false;
    public running = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: QueryTestData,
        private dialogRef: MatDialogRef<QueryTestDialogComponent>,
    ) {}

    ngAfterViewInit(): void {
        // The view exists by now, so there is no timing to get wrong.
        void this.rerun();
    }

    async rerun(): Promise<void> {
        this.running = true;
        try {
            await this.result?.run();
        } finally {
            this.running = false;
        }
    }

    onStatus(status: QueryRunStatus): void {
        this.meta = status.text;
        this.failed = status.failed;
    }

    close(): void {
        this.dialogRef.close();
    }
}
